/**
 * The CoOrdio product catalog as Cortex sees it — every product served by the **CoordioCore app**,
 * plus Cortex itself as the entry the user is already in.
 *
 * ── WHY this file is a copy ────────────────────────────────────────────────────────────────────
 *
 * The same list lives in CoordioCore at `frontend/libs/shell/src/product-modules.ts`. Cortex is a
 * **separate repository** and cannot import it, and the two apps must render the same switcher, so
 * the list is restated here deliberately rather than by accident.
 *
 * **Adding or renaming a product therefore means editing BOTH repositories.** That cost was
 * accepted rather than extracting a shared package. If this list and Core's ever disagree, Core's
 * copy is the source of truth — it is the one that decides which modules actually exist.
 *
 * ── How selection works here ───────────────────────────────────────────────────────────────────
 *
 * Cortex renders none of these products. Choosing one opens Core in a new tab at that product's
 * slug, so picking "Coordio Care" lands on Care rather than on Core's home page. Cortex's own entry
 * is present so the selector has something to display, but it is not a navigation target — you
 * cannot "go to" the app you are already in.
 */

/** Module ids — these strings must match CoordioCore's catalog exactly, or the deep link misses. */
export const COORDIO_CORE_MODULE = 'coordio-core';
export const COORDIO_CARE_MODULE = 'coordio-care';
export const COORDIO_CASE_MODULE = 'coordio-case';
export const COORDIO_CYCLE_MODULE = 'coordio-cycle';
export const COORDIO_CLEAR_MODULE = 'coordio-clear';
export const COORDIO_CROSSFLOW_MODULE = 'coordio-crossflow';
export const COORDIO_AUTOMATE_MODULE = 'coordio-automate';
export const COORDIO_ADMIN_MODULE = 'coordio-admin';

/** This app. Displayed as the current selection; never a navigation target. */
export const COORDIO_CORTEX_MODULE = 'coordio-cortex';

export type ProductModuleOption = Readonly<{
  value: string;
  label: string;
}>;

/**
 * URL slug per module, mirroring CoordioCore's `MODULE_URL_SLUGS` in reverse.
 *
 * WHY `cases` (plural) for Case: Core routes every case page under `/cases`, and maps BOTH `case`
 * and `cases` onto the module. The plural is used here because it is the segment that also lands on
 * a real page rather than falling through Core's `*` route to home.
 *
 * `coordio-admin` is intentionally absent — see {@link CORE_PRODUCT_OPTIONS}.
 */
const MODULE_SLUGS: Readonly<Record<string, string>> = {
  [COORDIO_CORE_MODULE]: 'core',
  [COORDIO_CARE_MODULE]: 'care',
  [COORDIO_CASE_MODULE]: 'cases',
  [COORDIO_CYCLE_MODULE]: 'cycle',
  [COORDIO_CLEAR_MODULE]: 'clear',
  [COORDIO_CROSSFLOW_MODULE]: 'crossflow',
  [COORDIO_AUTOMATE_MODULE]: 'automate',
};

/**
 * What the selector offers, in CoordioCore's own order, with Cortex last as the current app.
 *
 * WHY Coordio Admin is omitted: Core gates it behind a management-role check it performs against
 * the signed-in user's permissions. Cortex cannot evaluate that check — it has no access to Core's
 * permission service — so offering the entry here would show an option that 403s for most users.
 * Admin is reachable from inside Core, where the gate is applied honestly.
 */
export const CORE_PRODUCT_OPTIONS: readonly ProductModuleOption[] = [
  { value: COORDIO_CORE_MODULE, label: 'Coordio Core' },
  { value: COORDIO_CARE_MODULE, label: 'Coordio Care' },
  { value: COORDIO_CASE_MODULE, label: 'Coordio Case' },
  { value: COORDIO_CYCLE_MODULE, label: 'Coordio Cycle' },
  { value: COORDIO_CLEAR_MODULE, label: 'Coordio Clear' },
  { value: COORDIO_CROSSFLOW_MODULE, label: 'Coordio Crossflow' },
  { value: COORDIO_AUTOMATE_MODULE, label: 'Coordio Automate' },
];

/** Cortex's own entry, appended so the selector always has a label to show. */
export const CORTEX_PRODUCT_OPTION: ProductModuleOption = {
  value: COORDIO_CORTEX_MODULE,
  label: 'Coordio Cortex',
};

/**
 * Absolute URL of the CoordioCore page that serves `moduleId`, or null when there is nowhere to go.
 *
 * Returns null — meaning "offer nothing" — when `VITE_URL_CORE` is unset or when the id is Cortex's
 * own. The caller uses that to hide the switcher entirely rather than render dead options: a build
 * that does not know where Core lives cannot honestly offer to open it.
 */
export function resolveCoreModuleUrl(moduleId: string): string | null {
  if (moduleId === COORDIO_CORTEX_MODULE) return null;

  const base = String(import.meta.env.VITE_URL_CORE ?? '').trim().replace(/\/+$/, '');
  if (!base) return null;

  const slug = MODULE_SLUGS[moduleId];
  return slug ? `${base}/${slug}` : base;
}

/** True when this build knows where CoordioCore lives, and can therefore offer the switcher. */
export function hasCoreProductUrl(): boolean {
  return String(import.meta.env.VITE_URL_CORE ?? '').trim().length > 0;
}
